import { useState } from 'react';
import { ArrowUpRight, Globe, Apple, Android, WebPortal } from './icons';
import trackerjaxShot from '../assets/portfolio/trackerjax.jpg';
import equityworkxShot from '../assets/portfolio/equityworkx.jpg';
import drawmasterShot from '../assets/portfolio/drawmaster.jpg';
import codecreditShot from '../assets/portfolio/codecredit.jpg';
import madyokeShot from '../assets/portfolio/madyokegolf.jpg';
import styles from './Portfolio.module.css';

const PLATFORMS = {
  web: { label: 'Web App', Icon: Globe, mobile: false },
  ios: { label: 'iOS', Icon: Apple, mobile: true },
  android: { label: 'Android', Icon: Android, mobile: true },
  portal: { label: 'Web Portal', Icon: WebPortal, mobile: false },
};

const PRODUCTS = [
  {
    id: 'trackerjax',
    mark: 'TJ',
    gradient: 'linear-gradient(135deg,#e4564e,#b62f28)',
    name: 'TrackerJax',
    kicker: 'B2B SaaS',
    cat: 'B2B SaaS',
    url: 'trackerjax.com',
    href: 'https://trackerjax.com',
    shot: trackerjaxShot,
    desc: 'Assembly & field service management platform with real-time tracking, quality control, and mobile optimization.',
    platforms: ['web'],
  },
  {
    id: 'equityworkx',
    mark: 'EW',
    gradient: 'linear-gradient(135deg,#4f46e5,#312e81)',
    name: 'EquityWorkx',
    kicker: 'Fintech · Mobile',
    cat: 'Fintech · iOS + Android',
    url: 'equityworkx.com',
    href: 'https://equityworkx.com',
    shot: equityworkxShot,
    desc: 'Native iOS and Android apps — with a companion web portal — for mortgage optimization, helping homeowners build generational wealth through smart optimization and personalized financial insights.',
    platforms: ['ios', 'android', 'portal'],
  },
  {
    id: 'drawmaster',
    mark: 'DM',
    gradient: 'linear-gradient(135deg,#0f766e,#134e4a)',
    name: 'DrawMaster',
    kicker: 'Mobile App',
    cat: 'Mobile App · iOS + Android',
    url: 'drawmaster.app',
    href: 'https://drawmaster.app',
    shot: drawmasterShot,
    desc: "Native iOS and Android hunt-intelligence apps — plus a web portal — with a draw-odds calculator, deadline tracking, and strategic planning for Utah's complex draw systems.",
    platforms: ['ios', 'android', 'portal'],
  },
  {
    id: 'codecredit',
    mark: 'CC',
    gradient: 'linear-gradient(135deg,#0f172a,#334155)',
    name: 'CodeCredit',
    kicker: 'B2B SaaS',
    cat: 'B2B SaaS',
    url: 'code-credit.com',
    href: 'https://code-credit.com',
    shot: codecreditShot,
    desc: 'R&D tax credit tracking for software developers. IRS Form 6765 compliant — built by developers, for developers.',
    platforms: ['web'],
  },
  {
    id: 'madyoke',
    mark: 'MY',
    gradient: 'linear-gradient(135deg,#ca8a04,#854d0e)',
    name: 'Mad Yoke Golf',
    kicker: 'Web App',
    cat: 'Web App',
    url: 'madyokegolf.com',
    href: 'https://madyokegolf.com',
    shot: madyokeShot,
    desc: 'Custom scorecards for any golf adventure — real-time leaderboards, voice control, and composite course creation from your favorite local spots.',
    platforms: ['web'],
  },
];

const STACK = ['React', 'Next.js', 'Node.js', 'AWS', 'PostgreSQL', 'React Native'];

export default function Portfolio() {
  const [active, setActive] = useState(PRODUCTS[0].id);

  return (
    <section className={`section ${styles.pfSection}`} id="portfolio">
      <div className="wrap">
        <div className="reveal">
          <span className="eyebrow">Beyond NetSuite</span>
          <h2 className="section-title" style={{ marginTop: 16 }}>
            Products I've Built
          </h2>
          <p className="section-lead">
            Full-stack development demonstrated through production applications.
          </p>
        </div>

        <div className={styles.pfLayout}>
          <div className={`${styles.pfTabs} reveal`} role="tablist" aria-label="Portfolio products">
            {PRODUCTS.map((p) => (
              <button
                key={p.id}
                className={`${styles.pfTab} ${active === p.id ? styles.active : ''}`}
                role="tab"
                aria-selected={active === p.id}
                onClick={() => setActive(p.id)}
              >
                <span className={styles.pfMark} style={{ background: p.gradient }}>
                  {p.mark}
                </span>
                <span className={styles.meta}>
                  <b>{p.name}</b>
                  <span>{p.kicker}</span>
                </span>
              </button>
            ))}
          </div>

          <div className={`${styles.pfStage} reveal`}>
            {PRODUCTS.map((p) => (
              <article
                key={p.id}
                className={`${styles.pfPanel} ${active === p.id ? styles.active : ''}`}
                role="tabpanel"
                hidden={active !== p.id}
              >
                <div className={styles.pfHero} style={{ background: p.gradient }}>
                  <div className={styles.browser}>
                    <div className={styles.browserBar}>
                      <i />
                      <i />
                      <i />
                      <span className={styles.browserUrl}>{p.url}</span>
                    </div>
                    <img
                      className={styles.browserShot}
                      src={p.shot}
                      alt={`${p.name} live site — homepage screenshot`}
                      loading="lazy"
                      width="2880"
                      height="1800"
                    />
                  </div>
                </div>
                <div className={styles.pfInfo}>
                  <div className={styles.top}>
                    <h3>{p.name}</h3>
                    <span className={styles.cat}>{p.cat}</span>
                  </div>
                  <p>{p.desc}</p>
                  <div className={styles.pfPlatforms}>
                    {p.platforms.map((key) => {
                      const { label, Icon, mobile } = PLATFORMS[key];
                      return (
                        <span className={`${styles.plat} ${mobile ? styles.mobile : ''}`} key={key}>
                          <Icon /> {label}
                        </span>
                      );
                    })}
                  </div>
                  <a className={styles.pfVisit} href={p.href} target="_blank" rel="noopener noreferrer">
                    Visit {p.url} <ArrowUpRight />
                  </a>
                </div>
              </article>
            ))}

            <div className={styles.techStrip}>
              <span className={styles.lab}>Stack</span>
              {STACK.map((t) => (
                <span className="chip" key={t}>
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
